import Link from 'next/link'
import { useTranslation } from '@/i18n/client'
import { useUserStore } from '@/stores/user-store-provider'
import { LogoutAction, UpdateCurrencyAction } from '@/actions/user'
import { useRouter, usePathname } from 'next/navigation'
import { currencyArr, langArr } from '@/utils/settings'
import ErrorMessage from '@/components/ErrorMessage'

export default function SideBar({ lng, isSidebarOpen, closeSidebar }: { lng: string; isSidebarOpen: boolean; closeSidebar: () => void }) {
  const { t } = useTranslation(lng, 'header')
  const { t: footerT } = useTranslation(lng, 'footer')
  const { t: userT } = useTranslation(lng, 'user-profile')
  const { isLogin, name, updateIsLogin, updateCurrency, updateCurrencySign, updateExchangeRate } = useUserStore((state) => state)
  const router = useRouter()
  const pathName = usePathname()

  // 退出登录
  const logout = async () => {
    const resActionData: any = await LogoutAction()
    if (resActionData.success) {
      localStorage.removeItem('userStore')
      updateIsLogin(false)
      window.location.href = `/${lng}/login`
    }
  }

  // 切换语言
  const onLanguageChange = (key: string) => {
    const newPath = pathName.replace(/^\/(zh-CN|zh-HK|en)(\/|$)/, `/${key}$2`)
    router.push(newPath, { scroll: false })
  }

  // 修改货币 (the same as HeaderBase.tsx)
  const changeCurrency = async (currencyItem: any) => {
    const resActionData: any = await UpdateCurrencyAction({ currency: currencyItem.label.split('(')[0], currencySign: currencyItem.key })
    if (!resActionData.success) return <ErrorMessage error={resActionData.errorLangs[lng]} />
    if (resActionData.success) {
      updateCurrency(resActionData.result.currency)
      updateCurrencySign(currencyItem.key)
      const exchangeRate = Math.round((resActionData.result.remark / resActionData.result.remark2) * 1000000) / 1000000 // 保留6位小数
      updateExchangeRate(exchangeRate)
    }
  }

  return (
    <div className={`fixed bottom-0 left-0 top-0 z-[9999] ${isSidebarOpen ? 'w-[290px]' : 'w-0'} h-full overflow-y-auto bg-black transition-[width] duration-[400ms] ease-in-out`} onClick={closeSidebar}>
      <div className="block border-b border-b-[rgba(255,255,255,0.12)] px-5 py-4.5 text-center lg:hidden">
        {isLogin ? (
          <span className="text-[16px] leading-[1.25] text-red">{`${t('welcome')}：${name}`}</span>
        ) : (
          <p className="text-[16px] leading-[1.25] text-red">
            <Link href={`/${lng}/register`}>{t('register')}</Link> / <Link href={`/${lng}/login`}>{t('login')}</Link>
          </p>
        )}
      </div>
      {isLogin && (
        <nav className="block lg:hidden">
          <div className="border-b border-b-[rgba(255,255,255,0.12)] bg-[#313b47] px-5 py-[22px]">
            <h3 className="text-[16px] leading-[1.25] text-white">{userT('mine')}</h3>
          </div>
          <nav className="w-full overflow-hidden">
            <ul className="leading-[1.8]">
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-center`}>
                    {userT('myCenter')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/rent-to-own/list`}>
                    {userT('myRent')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-devices`}>
                    {userT('myDevices')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-orders`}>
                    {userT('myOrders')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-wishes`}>
                    {userT('myWishes')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-gift-history`}>
                    {userT('myGiftHistory')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-redeem-code`}>
                    {userT('myRedeemCode')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/my-cart`}>
                    {userT('myCart')}
                  </Link>
                </span>
              </li>
              <li className="w-full">
                <span className="relative block w-full">
                  <span onClick={logout} className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]">
                    {t('signOut')}
                  </span>
                </span>
              </li>
            </ul>
          </nav>
        </nav>
      )}
      <div className="border-b border-b-[rgba(255,255,255,0.12)] bg-[#313b47] px-5 py-[30px] lg:bg-black">
        <h3 className="text-[16px] leading-[1.25] text-gray">{t('websiteNavigation')}</h3>
      </div>
      <nav className="w-full overflow-hidden">
        <ul className="leading-[1.8]">
          <li className="w-full">
            <span className="relative block w-full">
              <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/all-books`}>
                {t('allBooks')}
              </Link>
            </span>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/inspirata-publications`}>
              {t('inspirata')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/all-books/bookLanguage/simplifiedChinese`}>
              {t('simplifiedChineseBooks')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/all-books/bookLanguage/traditionalChinese`}>
              {t('traditionalChineseBooks')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/all-books/bookLanguage/english`}>
              {t('englishBooks')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/free-books`}>
              {t('free')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/reading-partner`}>
              {t('readingPartner')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/rent-to-own`}>
              {t('rentToOwn')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/blogs`}>
              {t('blog')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/app-download`}>
              {t('download')}
            </Link>
          </li>
          <li className="w-full">
            <Link className="block border-b border-b-[rgba(255,255,255,0.12)] py-3.75 pl-[50px] pr-[65px] text-[14px] font-medium uppercase leading-[1.8] text-gray no-underline transition-[color] duration-[300ms]" href={`/${lng}/theses-dissertations`}>
              {footerT('thesesDissertations')}
            </Link>
          </li>
        </ul>
      </nav>
      <div className="block border-b border-b-[rgba(255,255,255,0.12)] bg-[#313b47] px-5 py-[22px] lg:hidden">
        <h3 className="text-[16px] leading-[1.25] text-white">{t('payCurrency')}</h3>
      </div>
      <div className="block py-7 text-center text-[14px] text-[#ffffff80] lg:hidden">
        {currencyArr.map((item: any, index: number) => (
          <span key={`${item.key}${Math.random()}`}>
            <Link
              className="text-[16px] leading-6"
              href="#"
              onClick={(e) => {
                e.preventDefault()
                changeCurrency(item)
              }}>
              {item.label}
            </Link>
            {index % 2 === 0 && index !== currencyArr.length - 1 && <>&nbsp;&nbsp;|&nbsp;&nbsp;</>}
            {index % 2 === 1 && index !== currencyArr.length - 1 && <br />}
          </span>
        ))}
      </div>
      <div className="block border-b border-b-[rgba(255,255,255,0.12)] bg-[#313b47] px-5 py-[22px] lg:hidden">
        <h3 className="text-[16px] leading-[1.25] text-white">{t('pageLanguage')}</h3>
      </div>
      <div className="block pb-[200px] pt-7 text-center text-[14px] text-[#ffffff80] lg:hidden">
        {langArr.map((item: any, index: number) => (
          <a
            className="text-[16px] leading-6"
            key={`${item.key}${Math.random()}`}
            href="#"
            onClick={(e) => {
              e.preventDefault()
              onLanguageChange(item.key)
            }}>
            {item.label}
            {(index === 0 || index === 1) && <>&nbsp;&nbsp;|&nbsp;&nbsp;</>}
          </a>
        ))}
      </div>
    </div>
  )
}
